// Individual Date Picker dropdown shown on calendar and week view
import { useMemo, useState } from "react";
import { ReactComponent as IconArrowDown } from "../assets/icons/icons-arrow-down.svg";
import { ReactComponent as IconArrowUp } from "../assets/icons/icons-arrow-up.svg";

import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  releaseTemporarySlot,
  selectTimeSlot,
  setCurrentlyOpenDateDropdown,
  setModalContents,
  setAppointmentType,
  setShuttleServiceSelected,
} from "../store/scheduler";
import AfterHoursModal from "./modals/AfterHoursModal";
import ShuttleUnavailableModal from "./modals/ShuttleUnavailableModal";
import TagManager from "react-gtm-module";

import "./DatePicker.css";

//=======================================================
// Button within date picker that contains a specific time
//=======================================================

const DateButton = ({
  label,
  handleSelected,
  inactive,
  time,
  date,
  dropoffDate,
  selected,
  shuttleAvailable,
  showShuttleIndicator,
}) => {
  // Click handler for our button
  const handleClicked = (e) => {
    e.preventDefault();

    if (handleSelected) {
      // Return the current time that we selected here
      handleSelected(time, date, dropoffDate);
    } else {
      console.warn("You need to pass in a handler for button clicks");
    }
  };

  return (
    <button
      onClick={handleClicked}
      className={`datepicker-btn ${selected ? "selected" : ""}`}
      disabled={inactive}
      aria-label={`time slot ${label}${
        shuttleAvailable ? ", shuttle available" : ""
      }`}
    >
      <span className="datepicker-btn-label">{label}</span>
      {showShuttleIndicator && shuttleAvailable && (
        <span className="datepicker-shuttle-indicator">
          <span className="shuttle-badge-text">S</span>
        </span>
      )}
    </button>
  );
};

//=======================================================
// Date Picker component exported and used by DateTime view
//=======================================================

export default function DatePicker({ data, alwaysOpen }) {
  const shuttleLogicEnabled = process.env.REACT_APP_SHUTTLE_LOGIC === "true";
  const dispatch = useDispatch();
  const { breakStartTime } = useSelector((state) => state.scheduler.franchise);
  const selectedSlot = useSelector((state) => state.scheduler.selectedSlot);
  const temporarySlotId = useSelector(
    (state) => state.scheduler.temporarySlotId
  );
  const {
    currentlyOpenDateDropdown,
    appointmentType,
    shuttleServiceSelected,
    slotsLoading,
  } = useSelector((state) => state.scheduler);

  // Derive legacy flags from appointmentType
  const isAfterHoursDropoff = appointmentType === "ahdo";
  const menuIsOpen = currentlyOpenDateDropdown === data.key;

  // User Clicked on a Date Button
  const handlePickedDate = (time) => {
    // Check if shuttle is selected and this slot doesn't have shuttle (only when shuttle logic is enabled)
    if (shuttleLogicEnabled && shuttleServiceSelected) {
      const selectedSlotData = data.slots.find(
        (slot) => slot.fromTime === time
      );
      if (selectedSlotData && selectedSlotData.shuttleAvailable === false) {
        // Show confirmation modal
        const modal = (
          <ShuttleUnavailableModal
            onConfirm={() => {
              // Deselect shuttle and select time
              dispatch(setShuttleServiceSelected(false));
              if (temporarySlotId) {
                dispatch(releaseTemporarySlot());
              }
              dispatch(
                selectTimeSlot({ date: data.key, time, dropoffDate: null })
              );
              dispatch(setModalContents(null));
            }}
            onCancel={() => dispatch(setModalContents(null))}
          />
        );
        dispatch(setModalContents(modal));
        return; // Don't select yet
      }
    }

    if (temporarySlotId) {
      dispatch(releaseTemporarySlot());
    }

    // If coming from after hours dropoff, switch back to regular dropoff
    if (isAfterHoursDropoff) {
      dispatch(setAppointmentType("dropoff"));
    }

    dispatch(selectTimeSlot({ date: data.key, time, dropoffDate: null }));
  };

  // Callback for after hours button
  const handleAfterHoursClicked = (time, date, dropoffDate) => {
    if (temporarySlotId) {
      dispatch(releaseTemporarySlot());
    }

    // Set appointment type to After Hours Drop Off
    dispatch(setAppointmentType("ahdo"));

    // Dispatch our slots date (day of dropoff), time (time for scheduler), and dropoffDate(day for scheduler)
    dispatch(selectTimeSlot({ date, time, dropoffDate }));

    // Check to make sure we had shuttle service before - if so, fire GTM event
    if (shuttleServiceSelected) {
      // GTM - Deselect shuttle service automatically
      const dataLayer = {
        event: "event_select_shuttle",
        shuttleServiceValue: false,
      };

      // Push to dataLayer
      TagManager.dataLayer({
        dataLayer,
      });
    }

    // Show our modal
    const modal = <AfterHoursModal />;

    dispatch(setModalContents(modal));
  };

  // User Clicked on a Dropdown Button
  const handleOpenMenu = (e) => {
    e.preventDefault();

    if (menuIsOpen) {
      dispatch(setCurrentlyOpenDateDropdown(""));
    } else {
      dispatch(setCurrentlyOpenDateDropdown(data.key));
    }
  };

  // Store our day.js date
  const date = dayjs(data.key);
  const today = dayjs();

  // Time difference from our current day
  const isToday = date.format("DDMMYYYY") === today.format("DDMMYYYY");
  const displayDay = date.format("ddd").toUpperCase();
  const displayDate = date.format("MMM D");
  const locationClosed = !data.slots.length;

  // Check to see if after hours dropoff is allowed
  const afterHours =
    data.afterHoursDropoff && data.afterHoursDropoff.isAfterHoursDropoff;

  // Filtered slot count respecting shuttle selection (only when shuttle logic is enabled)
  const filteredSlotCount = useMemo(() => {
    if (appointmentType === "ahdo" || locationClosed) return 0;
    return data.slots.filter(
      (item) =>
        item.fromTime.split(":")[0] !== breakStartTime.split(":")[0] &&
        item.availableSlots > 0 &&
        (!shuttleLogicEnabled ||
          !shuttleServiceSelected ||
          item.shuttleAvailable)
    ).length;
  }, [
    data.slots,
    breakStartTime,
    appointmentType,
    locationClosed,
    shuttleServiceSelected,
    shuttleLogicEnabled,
  ]);

  // Check if there are any available time slots (location open but all slots booked)
  const hasAvailableSlots = filteredSlotCount > 0;

  // Determine if we show carot button
  const showCarot = useMemo(() => {
    // No carot on calendar
    if (alwaysOpen) return false;

    if (appointmentType === "ahdo") {
      return afterHours;
    }

    // Hide caret if location is closed (without after hours)
    if (locationClosed && !afterHours) return false;

    // Hide caret if no available time slots (all slots booked)
    return !(!hasAvailableSlots && !afterHours);
  }, [
    alwaysOpen,
    locationClosed,
    hasAvailableSlots,
    afterHours,
    appointmentType,
  ]);

  // Generate our date buttons
  const generateDateButtons = useMemo(() => {
    // AHDO mode - do not render regular time slots (loading handled by getAfterHoursButton)
    if (appointmentType === "ahdo") {
      return null;
    }

    const validSlots = data.slots.filter(
      (item) =>
        item.fromTime.split(":")[0] !== breakStartTime.split(":")[0] &&
        item.availableSlots > 0 &&
        (!shuttleLogicEnabled ||
          !shuttleServiceSelected ||
          item.shuttleAvailable)
    );

    if (!validSlots.length) {
      return (
        <div className="datepicker-no-slots" aria-label="no times available">
          No Times Available
        </div>
      );
    }

    // Filter out break time slots
    return validSlots.map((slot, index) => {
      // Convert our hours into the correct Label for 12 hour time
      const slotHours = Number(slot.fromTime.substring(0, 2));
      const minutes = slot.fromTime.split(":")[1];
      const hours = slotHours < 12 ? slotHours : ((slotHours + 11) % 12) + 1;
      const suffix = slot.fromTime.substr(0, 2) > 11 ? "PM" : "AM";
      // const label = `${hours}:00 ${suffix}`;
      const label = `${hours}:${minutes} ${suffix}`;

      return (
        <DateButton
          label={label}
          selected={
            selectedSlot &&
            selectedSlot.date === data.key &&
            selectedSlot.time === slot.fromTime &&
            !selectedSlot.dropoffDate
          }
          key={`${slot.fromTime}_${index}`}
          time={slot.fromTime}
          inactive={slot.availableSlots <= 0 || locationClosed || slotsLoading}
          handleSelected={handlePickedDate}
          shuttleAvailable={slot.shuttleAvailable ?? false}
          showShuttleIndicator={
            shuttleLogicEnabled && appointmentType === "dropoff"
          }
        />
      );
    });

    // Filter out break time slots
    // return data.slots
    //   .filter(
    //     (item) =>
    //       item.fromTime.split(":")[0] !== breakStartTime.split(":")[0] &&
    //       item.availableSlots > 0
    //   )
    //   .map((slot, index) => {
    //     // Convert our hours into the correct Label for 12 hour time
    //     const slotHours = Number(slot.fromTime.substring(0, 2));
    //     const minutes = slot.fromTime.split(":")[1];
    //     const hours = slotHours < 12 ? slotHours : ((slotHours + 11) % 12) + 1;
    //     const suffix = slot.fromTime.substr(0, 2) > 11 ? "PM" : "AM";
    //     // const label = `${hours}:00 ${suffix}`;
    //     const label = `${hours}:${minutes} ${suffix}`;
    //
    //     return (
    //       <DateButton
    //         label={label}
    //         selected={
    //           selectedSlot &&
    //           selectedSlot.date === data.key &&
    //           selectedSlot.time === slot.fromTime &&
    //           !selectedSlot.dropoffDate
    //         }
    //         key={`${slot.fromTime}_${index}`}
    //         time={slot.fromTime}
    //         inactive={slot.availableSlots <= 0 || locationClosed}
    //         handleSelected={handlePickedDate}
    //       />
    //     );
    //   });
  }, [
    isToday,
    locationClosed,
    today,
    selectedSlot,
    data,
    appointmentType,
    slotsLoading,
    breakStartTime,
  ]);

  // Returns our after hours button
  const getAfterHoursButton = () => {
    if (appointmentType !== "ahdo") return null;

    // If no afterHoursDropoff data OR not available, show empty state
    if (
      !data.afterHoursDropoff ||
      !data.afterHoursDropoff.isAfterHoursDropoff
    ) {
      return (
        <div
          className="datepicker-no-slots"
          aria-label="no after hours available"
        >
          After Hours Not Available
        </div>
      );
    }

    return (
      <DateButton
        label="After Hours"
        selected={
          selectedSlot &&
          selectedSlot.date === data.key &&
          selectedSlot.time === data.afterHoursDropoff.fromTime &&
          selectedSlot.dropoffDate
        }
        time={data.afterHoursDropoff.fromTime}
        date={data.key}
        dropoffDate={data.afterHoursDropoff.afterHoursDropoffDate}
        inactive={slotsLoading}
        handleSelected={handleAfterHoursClicked}
      />
    );
  };

  // Returns arrow depending on selection made
  const getToggleIcon = useMemo(() => {
    return menuIsOpen ? <IconArrowUp /> : <IconArrowDown />;
  }, [menuIsOpen]);

  // Calculate our display label for the current day
  const getDisplayLabel = () => {
    // Show loading state
    if (slotsLoading) {
      return "Loading availability...";
    }

    // "ahdo" appointment type
    if (appointmentType === "ahdo") {
      if (afterHours) {
        return "After Hours Drop Off Available";
      } else {
        return "After Hours Drop Off Not Available";
      }
    }

    // Default labels
    if (locationClosed && !afterHours) {
      return data.displayLabel === "Location Closed"
        ? data.displayLabel
        : `Location Closed - ${data.displayLabel}`;
    }

    if (locationClosed && data.displayLabel !== "0 available times") {
      return `Location Closed - ${data.displayLabel}`;
    }

    // if (afterHours && !locationClosed && data.availableTimes === 0) {
    //   return "After Hours Drop Off Only";
    // }

    if (!locationClosed) {
      return `${filteredSlotCount} available ${
        filteredSlotCount === 1 ? "time" : "times"
      }`;
    }

    // After hours dropoff label modifications
    return "Closed, After Hours Drop Off Only";
  };

  return (
    <div className="datepicker">
      <div
        className="datepicker-head"
        onClick={showCarot ? handleOpenMenu : undefined}
        onKeyDown={showCarot ? handleOpenMenu : undefined}
        style={{ cursor: showCarot ? "pointer" : "default" }}
        role={"presentation"}
      >
        <h2>{displayDay}</h2>
        <p className="datepicker-date">{isToday ? "Today" : displayDate}</p>
        <p
          className={`datepicker-times-available ${
            (locationClosed && !afterHours) ||
            (isAfterHoursDropoff && !afterHours)
              ? "unavailable"
              : ""
          }`}
        >
          {getDisplayLabel()}
        </p>
        {showCarot && <div className="toggle-icon">{getToggleIcon}</div>}
      </div>

      {menuIsOpen && (
        <div className="datepicker-button-container">
          {slotsLoading ? (
            <div className="datepicker-no-slots" aria-label="loading times">
              Loading times...
            </div>
          ) : (
            <>
              {generateDateButtons}
              {getAfterHoursButton()}
            </>
          )}
        </div>
      )}
    </div>
  );
}
